import React, { useState, useCallback, useMemo } from "react";
import { DrilldownList, SearchActionsToolbar, makeFilterToolbarProps } from "@/components/UI/DrilldownList";
import { DrilldownDepthContext } from "@/components/UI/DrilldownList/context/DrilldownDepthContext";
import { FilterListItem } from "../DocHierarchyFilter/comps/PanelItem";
import { TopicClusterFilter } from "../TopicClusterFilter";
import { useAnnoHierarchyOptions } from "../../utils/useAnnoHierarchyOptions";
import { getDisplayText, getParentData } from "../../utils/annoHierarchyHelpers";
import { useDebounce } from "@/utils/customHooks";
import { useInFlightToggle } from "@/utils/customHooks/useInFlightToggle";
import { useListControls } from "@/utils/customHooks/useListControls";
import { FILTER_IDS } from "@okf/ootils";

const COMMON_ANNO_FILTER_ICONS = {
  usersType: "User",
  staticType: "File",
};

const getCommonAnnoFilterIcon = (filter) => {
  return COMMON_ANNO_FILTER_ICONS[filter?.source?.filterType] || "FolderSimple";
};

export const AnnoHierarchyFilter = ({
  filterConfig,
  value: selectedValues,
  onChange,
  // --- Scoped rendering (optional) ---
  scopeToLevel = null,
  initialSelectedTheme = null,
  initialSelectedSubTheme = null,
}) => {
  const { source = {} } = filterConfig;
  const {
    annotationTagsCount = {},
    levelLabels = {},
    commonAnnotationFilters = [],
    hierarchyLevels = [],
    // Split mode: hoist the sub-themes LEVEL to the panel root — every
    // sub-theme is its own top-level row (selectable + drills to its tags)
    // instead of hiding behind one "Independent Sub-Themes" folder entry.
    exposeSubThemesAtRoot = false,
    // Split mode: the Topics (clustering-tree) filter config, embedded so it
    // renders directly below the hierarchy rows, above the common filters.
    topicClusteringFilterConfig = null,
  } = source;

  // Tabs attached to a hierarchy level (e.g. sub-themes split into Thematics/Attributes).
  const attributeThematicTabs = useMemo(
    () => hierarchyLevels.find((l) => l.level === "subThemes")?.tabs || null,
    [hierarchyLevels]
  );

  const [openLevel, setOpenLevel] = useState(scopeToLevel ?? null);
  const [selectedTheme, setSelectedTheme] = useState(initialSelectedTheme ?? null);
  const [selectedSubTheme, setSelectedSubTheme] = useState(initialSelectedSubTheme ?? null);

  // --- Per-level search state (immediate for input, debounced for backend) ---
  const debounce = useDebounce();
  const [searchInputThemes, setSearchInputThemes] = useState("");
  const [searchInputSubThemes, setSearchInputSubThemes] = useState("");
  const [searchInputTags, setSearchInputTags] = useState("");
  const [debouncedSearchThemes, setDebouncedSearchThemes] = useState("");
  const [debouncedSearchSubThemes, setDebouncedSearchSubThemes] = useState("");
  const [debouncedSearchTags, setDebouncedSearchTags] = useState("");

  const handleSearchThemes = useCallback((v) => {
    setSearchInputThemes(v);
    debounce(() => setDebouncedSearchThemes(v));
  }, [debounce]);

  const handleSearchSubThemes = useCallback((v) => {
    setSearchInputSubThemes(v);
    debounce(() => setDebouncedSearchSubThemes(v));
  }, [debounce]);

  const handleSearchTags = useCallback((v) => {
    setSearchInputTags(v);
    debounce(() => setDebouncedSearchTags(v));
  }, [debounce]);

  const hierarchyConfig = useMemo(
    () => ({
      themes: !!annotationTagsCount.themes,
      subThemes: !!annotationTagsCount.subThemes,
      tags: !!annotationTagsCount.tags,
    }),
    [annotationTagsCount]
  );
  // openLevel may be "subThemes" or "subThemes:<tabKey>" when sub-themes tabs are configured.
  const activeAttributeThematicTabKey = useMemo(
    () => (typeof openLevel === "string" && openLevel.startsWith("subThemes:") ? openLevel.split(":")[1] : null),
    [openLevel]
  );
  const activeAttributeThematicTabFindQuery = useMemo(() => {
    if (!attributeThematicTabs || !activeAttributeThematicTabKey) return null;
    return attributeThematicTabs.find((t) => t.key === activeAttributeThematicTabKey)?.findQuery || null;
  }, [attributeThematicTabs, activeAttributeThematicTabKey]);

  // Derive fetch flags from navigation state
  const fetchConfig = useMemo(() => {
    const isViewingThemes = openLevel === "themes";
    // Root-exposed sub-themes are visible from mount, so their fetch is eager.
    const isViewingSubThemes =
      openLevel === "subThemes" || activeAttributeThematicTabKey !== null || exposeSubThemesAtRoot;
    const isViewingTags = openLevel === "tags";

    const hasDrilledIntoTheme = !!selectedTheme;
    const hasDrilledIntoSubTheme = !!selectedSubTheme;
    const hierarchySkipsSubThemes = !hierarchyConfig.subThemes;

    return {
      enableThemesFetch: isViewingThemes,
      enableSubThemesFetch: isViewingSubThemes || (isViewingThemes && hasDrilledIntoTheme),
      enableTagsFetch:
        isViewingTags ||
        (isViewingSubThemes && hasDrilledIntoSubTheme) ||
        (isViewingThemes && hasDrilledIntoTheme && hierarchySkipsSubThemes) ||
        (isViewingThemes && hasDrilledIntoSubTheme),
      fetchIndependentSubThemes: isViewingSubThemes && !hasDrilledIntoTheme,
      fetchIndependentTags: isViewingTags && !hasDrilledIntoSubTheme,
    };
  }, [openLevel, selectedTheme, selectedSubTheme, hierarchyConfig.subThemes, activeAttributeThematicTabKey, exposeSubThemesAtRoot]);

  const {
    optionsData,
    isLoading,
    isFetchingNextPage,
    observerRef,
    hierarchyConfig: { hasThemes, hasSubThemes, hasTags, hasIndependentSubThemes, hasIndependentTags },
  } = useAnnoHierarchyOptions({
    hierarchyConfig,
    selectedThemeId: selectedTheme?._id ?? null,
    selectedSubThemeId: selectedSubTheme?._id ?? null,
    searchTerms: {
      themes: "",
      subThemes: "",
      tags: debouncedSearchTags,
    },
    activeAttributeThematicTabFindQuery,
    ...fetchConfig,
  });

  // --- Per-level onChange factory ---

  const getValuesForFilterId = useCallback(
    (filterId) => {
      if (!selectedValues || typeof selectedValues !== "object") return [];
      if (Array.isArray(selectedValues)) return selectedValues;
      return selectedValues[filterId] || [];
    },
    [selectedValues]
  );

  const toggle = useInFlightToggle();

  const makeOnChange = useCallback(
    (filterId) => (option) => {
      // Build parent context from drilldown state to fill gaps
      // that getParentData(option) may miss due to incomplete backend populates
      const parentContext = {};
      if (selectedTheme) {
        parentContext.parentThemeId = selectedTheme._id;
        parentContext.parentThemeDisplay = getDisplayText(selectedTheme);
      }
      if (selectedSubTheme) {
        parentContext.parentSubThemeId = selectedSubTheme._id;
        parentContext.parentSubThemeDisplay = getDisplayText(selectedSubTheme);
      }

      const newValues = toggle({
        key: filterId,
        fallbackValues: getValuesForFilterId(filterId),
        option,
        buildEntry: (o, id) => ({
          value: id,
          display: getDisplayText(o),
          tagId: o.tagId ?? id,
          ...getParentData(o),
          ...parentContext,
        }),
      });
      onChange(filterId, newValues);
    },
    [getValuesForFilterId, onChange, selectedTheme, selectedSubTheme, toggle]
  );

  const onChangeThemes = useMemo(() => makeOnChange(FILTER_IDS.themes), [makeOnChange]);
  const onChangeSubThemes = useMemo(() => makeOnChange(FILTER_IDS.subThemes), [makeOnChange]);
  const onChangeTags = useMemo(() => makeOnChange(FILTER_IDS.tags), [makeOnChange]);

  // --- Per-level list controls (sort/showSelectedFirst + selectAll/clearAll/invert) ---
  // One hook instance per hierarchy level, since each maintains its own toolbar
  // state and operates against its own filterId's selection slice.
  const itemKey = (o) => o._id ?? o.value;
  const themesValue = getValuesForFilterId(FILTER_IDS.themes);
  const subThemesValue = getValuesForFilterId(FILTER_IDS.subThemes);
  const tagsValue = getValuesForFilterId(FILTER_IDS.tags);

  const onListChangeThemes = useCallback(
    (next) => onChange(FILTER_IDS.themes, next),
    [onChange]
  );
  const onListChangeSubThemes = useCallback(
    (next) => onChange(FILTER_IDS.subThemes, next),
    [onChange]
  );
  const onListChangeTags = useCallback(
    (next) => onChange(FILTER_IDS.tags, next),
    [onChange]
  );

  const themesCtl = useListControls({
    items: optionsData.themes || [],
    value: themesValue,
    onChange: onListChangeThemes,
    getKey: itemKey,
    getDisplay: getDisplayText,
  });
  const subThemesCtl = useListControls({
    items: optionsData.subThemes || [],
    value: subThemesValue,
    onChange: onListChangeSubThemes,
    getKey: itemKey,
    getDisplay: getDisplayText,
  });
  // Tags need a richer entry shape (tagId + parent context) for downstream consumers.
  const tagsCtl = useListControls({
    items: optionsData.tags || [],
    value: tagsValue,
    onChange: onListChangeTags,
    getKey: itemKey,
    getDisplay: getDisplayText,
    mapEntry: (o) => ({
      value: itemKey(o),
      display: getDisplayText(o),
      tagId: o.tagId ?? itemKey(o),
      ...getParentData(o),
    }),
  });

  // --- Root meta options ---

  const metaOptions = useMemo(() => {
    const opts = [];
    if (hasThemes)
      opts.push({
        value: "themes",
        display: levelLabels.themes || "Themes",
        icon: "FolderSimple",
        hasChildren: true,
      });
    // Show sub-themes/tags as root options when:
    // - No parent level exists (sole entry point), OR
    // - At least one independent (orphan) item exists
    // Root-exposed sub-themes render as their own list instead (see JSX) —
    // no folder entry here.
    if (hasSubThemes && !exposeSubThemesAtRoot && (!hasThemes || hasIndependentSubThemes)) {
      if (attributeThematicTabs) {
        attributeThematicTabs.forEach((tab) => {
          opts.push({
            value: `subThemes:${tab.key}`,
            display: tab.display,
            icon: "FolderSimple",
            hasChildren: true,
          });
        });
      } else {
        opts.push({
          value: "subThemes",
          display: levelLabels.subThemes || "Sub-Themes",
          icon: "FolderSimple",
          hasChildren: true,
        });
      }
    }
    if (hasTags && ((!hasSubThemes && !hasThemes) || hasIndependentTags))
      opts.push({
        value: "tags",
        display: levelLabels.tags || "Tags",
        icon: "Tag",
        hasChildren: true,
      });
    return opts;
  }, [hasThemes, hasSubThemes, hasTags, hasIndependentSubThemes, hasIndependentTags, levelLabels, attributeThematicTabs, exposeSubThemesAtRoot]);

  // --- Recursive getChildLevel ---

  const getTagsLevel = useCallback(
    () => {
      const tagOptions = tagsCtl.orderedItems.map((o) => ({
        ...o,
        icon: "Tag",
        hasChildren: false,
      }));

      return {
        options: tagOptions,
        value: tagsValue,
        isMulti: true,
        isLoading: isLoading.tags,
        isFetchingNextPage: isFetchingNextPage.tags,
        observerRef: observerRef.tags,
        onChange: onChangeTags,
        emptyMessage: "No tags",
        showSelectedFirst: tagsCtl.showSelectedFirst,
        headerContent: (
          <SearchActionsToolbar
            {...makeFilterToolbarProps(tagsCtl, {
              searchValue: searchInputTags,
              onSearch: handleSearchTags,
            })}
          />
        ),
      };
    },
    [
      tagsCtl, tagsValue, isLoading.tags, isFetchingNextPage.tags,
      observerRef.tags, onChangeTags, searchInputTags, handleSearchTags,
    ]
  );

  const getSubThemesLevel = useCallback(
    () => {
      const opts = subThemesCtl.orderedItems
        .filter((o) =>
          !searchInputSubThemes ||
          (getDisplayText(o) || "").toLowerCase().includes(searchInputSubThemes.toLowerCase())
        )
        .map((o) => ({ ...o, icon: o.icon || "FolderSimple", hasChildren: hasTags }));

      return {
        options: opts,
        value: subThemesValue,
        isMulti: true,
        isLoading: isLoading.subThemes,
        isFetchingNextPage: isFetchingNextPage.subThemes,
        observerRef: observerRef.subThemes,
        onChange: onChangeSubThemes,
        onDrilldown: (option) => setSelectedSubTheme(option),
        getChildLevel: hasTags ? getTagsLevel : undefined,
        emptyMessage: "No sub-themes",
        showSelectedFirst: subThemesCtl.showSelectedFirst,
        headerContent: (
          <SearchActionsToolbar
            {...makeFilterToolbarProps(subThemesCtl, {
              searchValue: searchInputSubThemes,
              onSearch: handleSearchSubThemes,
            })}
          />
        ),
      };
    },
    [
      subThemesCtl, subThemesValue, isLoading.subThemes,
      isFetchingNextPage.subThemes, observerRef.subThemes, onChangeSubThemes,
      hasTags, getTagsLevel, searchInputSubThemes, handleSearchSubThemes,
    ]
  );

  const getThemesLevel = useCallback(
    () => {
      const opts = themesCtl.orderedItems
        .filter((o) =>
          !searchInputThemes ||
          (getDisplayText(o) || "").toLowerCase().includes(searchInputThemes.toLowerCase())
        )
        .map((o) => ({ ...o, icon: o.icon || "FolderSimple", hasChildren: hasSubThemes || hasTags }));

      return {
        options: opts,
        value: themesValue,
        isMulti: true,
        isLoading: isLoading.themes,
        isFetchingNextPage: isFetchingNextPage.themes,
        observerRef: observerRef.themes,
        onChange: onChangeThemes,
        onDrilldown: (option) => {
          setSelectedTheme(option);
          setSelectedSubTheme(null);
        },
        getChildLevel: hasSubThemes ? getSubThemesLevel : hasTags ? getTagsLevel : undefined,
        emptyMessage: "No themes",
        showSelectedFirst: themesCtl.showSelectedFirst,
        headerContent: (
          <SearchActionsToolbar
            {...makeFilterToolbarProps(themesCtl, {
              searchValue: searchInputThemes,
              onSearch: handleSearchThemes,
            })}
          />
        ),
      };
    },
    [
      themesCtl, themesValue, isLoading.themes,
      isFetchingNextPage.themes, observerRef.themes, onChangeThemes,
      hasSubThemes, hasTags, getSubThemesLevel, getTagsLevel,
      searchInputThemes, handleSearchThemes,
    ]
  );

  const getChildLevel = useCallback(
    (metaOption) => {
      const level = metaOption.value;
      if (level === "themes") return getThemesLevel();
      if (level === "subThemes" || (typeof level === "string" && level.startsWith("subThemes:"))) return getSubThemesLevel();
      if (level === "tags") return getTagsLevel();
      return null;
    },
    [getThemesLevel, getSubThemesLevel, getTagsLevel]
  );

  const handleMetaDrilldown = useCallback((entry) => {
    setOpenLevel(entry.value);
    setSelectedTheme(null);
    setSelectedSubTheme(null);
    setSearchInputThemes("");
    setSearchInputSubThemes("");
    setSearchInputTags("");
    setDebouncedSearchThemes("");
    setDebouncedSearchSubThemes("");
    setDebouncedSearchTags("");
  }, []);

  if (scopeToLevel) {
    const scopedLevel = getChildLevel({ value: scopeToLevel });
    if (!scopedLevel) return null;
    return (
      <DrilldownList
        {...scopedLevel}
        folderSelectionMode={"exclusive"}
      />
    );
  }

  const hasHierarchyLevels = metaOptions.length > 0;

  return (
    <DrilldownDepthContext.Provider value={0}>
      {/* Split mode: the sub-themes level itself sits at the root — every
          sub-theme is a DRILL-ONLY row (no checkbox; whole-sub-theme
          selection isn't offered at this level) opening its tags, which
          remain multi-selectable via anno_tags. Reuses the drilled-in
          level config minus its selection wiring and toolbar. */}
      {exposeSubThemesAtRoot && hasSubThemes && (() => {
        const {
          value: _value,
          onChange: _onChange,
          isMulti: _isMulti,
          showSelectedFirst: _showSelectedFirst,
          headerContent: _headerContent,
          ...subThemesRootLevel
        } = getSubThemesLevel();
        return (
          <DrilldownList
            {...subThemesRootLevel}
            isMulti={false}
            folderSelectionMode={"exclusive"}
          />
        );
      })()}
      {hasHierarchyLevels && (
        <DrilldownList
          options={metaOptions}
          isMulti={false}
          onDrilldown={handleMetaDrilldown}
          getChildLevel={getChildLevel}
          folderSelectionMode={"exclusive"}
        />
      )}
      {/* Topics (clustering tree) sits right below the hierarchy rows,
          above the common filters. Same value/onChange contract as the
          common filters: its slice of the panel-level selection object. */}
      {topicClusteringFilterConfig && (
        <TopicClusterFilter
          filterConfig={topicClusteringFilterConfig}
          value={getValuesForFilterId(topicClusteringFilterConfig.filterId)}
          onChange={onChange}
        />
      )}
      <div style={{ display: "flex", flexDirection: "column" }}>
      {commonAnnotationFilters.map((filter) => (
        <FilterListItem
          key={filter.filterId}
          filter={filter}
          selectedValues={selectedValues || {}}
          onChange={onChange}
          icon={getCommonAnnoFilterIcon(filter)}
        />
      ))}
      </div>
    </DrilldownDepthContext.Provider>
  );
};
